import * as React from 'react';
import { useState, useEffect } from 'react';
import { bdAxios } from '../components/utils/Axios';
import { getToken } from '../components/utils/Common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faFloppyDisk, faEye, faXmark, faSearch } from '@fortawesome/free-solid-svg-icons'; // FontAwesome icons

interface RatioLabel {
  meta_key: string;
  num_row: string;
  label: string;
  description: string;
  type: string;
  display_row?: boolean;   // new
  display_desc?: boolean;  // new
}

interface AdminRatioLabelsProps {
  onBack: () => void;
}

    const AdminRatioLabels: React.FC<AdminRatioLabelsProps> = ({ onBack }) => {
    const [labels, setLabels] = useState<RatioLabel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [updatedLabels, setUpdatedLabels] = useState<Record<string, Partial<RatioLabel>>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');


  useEffect(() => {
    const token = getToken();
    if (!token) return;

    bdAxios.get('ratios/labels', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      // Ensure display_row / display_desc defaults
      const data: RatioLabel[] = res.data.map((l: any) => ({
        ...l,
        display_row: l.display_row === 1,
        display_desc: l.display_desc === 1
      }));
      setLabels(data);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to fetch labels:', err);
      setLoading(false);
    });
  }, []);

  const handleFieldChange = (key: string, field: keyof RatioLabel, value: string | boolean) => {
    setUpdatedLabels(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
    //setLabels(prev => prev.map(l => l.meta_key === key ? { ...l, [field]: value } : l));
  };

const saveField = async (key: string) => {
  const token = getToken();
  if (!token || !updatedLabels[key]) return;

  try {
    // Find the current label from state
    const currentLabel = labels.find(l => l.meta_key === key);
    if (!currentLabel) return;

    // Merge current state with updates, converting booleans to 0/1 for the API
    const payload = {
      meta_key: key,
      num_row: updatedLabels[key].num_row ?? currentLabel.num_row ?? null,
      label: updatedLabels[key].label ?? currentLabel.label,
      description: updatedLabels[key].description ?? currentLabel.description,
      display_row: updatedLabels[key].display_row !== undefined
        ? (updatedLabels[key].display_row ? 1 : 0)
        : (currentLabel.display_row ? 1 : 0),
      display_desc: updatedLabels[key].display_desc !== undefined
        ? (updatedLabels[key].display_desc ? 1 : 0)
        : (currentLabel.display_desc ? 1 : 0),
    };

    // Send update to backend
    await bdAxios.post('ratios/labels/update', payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    // Update local state — keep booleans
    setLabels(prev =>
      prev.map(l =>
        l.meta_key === key
          ? {
              ...l,
              num_row: payload.num_row,
              label: payload.label,
              description: payload.description,
              display_row: payload.display_row === 1,
              display_desc: payload.display_desc === 1,
            }
          : l
      )
    );

    // Remove from updatedLabels and exit edit mode
    setUpdatedLabels(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    setEditIndex(null);

  } catch (err) {
    console.error('Failed to save label', err);
  }
};

const totalCount = labels.length;
const visibleCount = labels.filter(l => l.display_row).length;
const hiddenCount = labels.filter(l => !l.display_row).length;


const filteredLabels = labels.filter(label => {
  // Search filter
  const matchesSearch =
    label.label?.toLowerCase().includes(searchTerm.toLowerCase());

  // Visibility filter
  const matchesVisibility =
    visibilityFilter === 'all' ||
    (visibilityFilter === 'visible' && label.display_row) ||
    (visibilityFilter === 'hidden' && !label.display_row);

  return matchesSearch && matchesVisibility;
});



  if (loading) return <div>Loading labels...</div>;

  return (
    <div className="ratio-labels-admin">
        <div className="ratio-labels-admin__header" style={{ marginBottom: '20px' }}>
            <h2>Ratios - Gérer les libellés</h2>
            <p>Cette section sert à éditer les libellés (titre et description) des données de ratios.</p>
            <p>* Affecte les libellés de <b>l'export des ratios</b>, et de <b>la saisie de données</b>.</p>
        </div> 

        <div style={{ marginBottom: '20px', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}>
    
            {/* Search */}
            <div style={{ marginBottom: '10px' }}>
                <input
                type="text"
                placeholder="Rechercher par libellé..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ padding: '5px', width: '250px' }}
                />
                <FontAwesomeIcon
                        icon={faSearch}
                        
                        style={{
                            marginLeft: '10px',
                        }}
                    />

            </div>

            {/* Visibility Radio */}
            <div>
                <label style={{ marginRight: '15px' }}>
                <input
                    type="radio"
                    value="all"
                    checked={visibilityFilter === 'all'}
                    onChange={() => setVisibilityFilter('all')}
                /> Tous ({totalCount})
                </label>

                <label style={{ marginRight: '15px' }}>
                <input
                    type="radio"
                    value="visible"
                    checked={visibilityFilter === 'visible'}
                    onChange={() => setVisibilityFilter('visible')}
                /> Visibles ({visibleCount})
                </label>

                <label>
                <input
                    type="radio"
                    value="hidden"
                    checked={visibilityFilter === 'hidden'}
                    onChange={() => setVisibilityFilter('hidden')}
                /> Cachés ({hiddenCount})
                </label>
            </div>

        </div>
   

      {filteredLabels.map((label, index) => {
        const isEditing = editIndex === index;
        const updated = updatedLabels[label.meta_key] || {};
        const currentDisplayRow =
            updated.display_row !== undefined
            ? updated.display_row
            : label.display_row;

        const currentDisplayDesc =
            updated.display_desc !== undefined
            ? updated.display_desc
            : label.display_desc;


        const hasChanges =
            'num_row' in updated ||
            'label' in updated ||
            'description' in updated ||
            'display_row' in updated ||
            'display_desc' in updated;

        // Keep original values for cancel
        const originalDisplayRow = label.display_row ? 1 : 0;
        const originalDisplayDesc = label.display_desc ? 1 : 0;

    return (
        <div key={label.meta_key} className="item_ratio formRow_ratiosLabel" style={{ border: '1px solid #ccc', padding: '10px', marginBottom: '10px', borderRadius: '5px' }}>
            <div style={{ marginBottom: '5px' }}>
                <strong>Nom du ratio :</strong> {label.meta_key} {label.type === 'title' ? '- Titre' : ''}
            </div>

            <div style={{ marginBottom: '5px' }}>
                <label style={{ fontWeight: 700 }}>Numéro du champ : </label>
                {isEditing ? (
                    <input
                    type="text"
                    value={updated.num_row ?? label.num_row ?? ''}
                    onChange={e => handleFieldChange(label.meta_key, 'num_row', e.target.value)}
                    placeholder="Numéro de ligne"
                    style={{ width: '80px' }}
                    />
                ) : (
                    <span>{label.num_row ? label.num_row : '-'}</span>
                )}
            </div>

            <div style={{ marginBottom: '5px' }}>
                <label style={{ fontWeight: 700 }}>Libellé du ratio : </label>
                {isEditing ? (
                <input type="text" value={updated.label ?? label.label} onChange={e => handleFieldChange(label.meta_key, 'label', e.target.value)} />
                ) : <span>{label.label}</span>}
            </div>

            <div style={{ marginBottom: '5px' }}>
                <label style={{ fontWeight: 700 }}>Description du ratio : </label>
                {isEditing ? (
                <input type="text" value={updated.description ?? label.description} onChange={e => handleFieldChange(label.meta_key, 'description', e.target.value)} />
                ) : <span>{label.description}</span>}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '5px' }}>
                <div>
                    <span title="visibilité dans l'export">Rangée : </span>
                    <FontAwesomeIcon
                        icon={faEye}
                        title={currentDisplayRow ? 'Visible' : 'Invisible'}
                        style={{
                            cursor: isEditing ? 'pointer' : 'not-allowed',
                            opacity: currentDisplayRow ? 1 : 0.5,
                            color: currentDisplayRow ? 'green' : 'gray',
                            marginLeft: '5px'
                        }}
                        onClick={() => {
                            if (!isEditing) return;
                            handleFieldChange(
                            label.meta_key,
                            'display_row',
                            !currentDisplayRow
                            );
                        }}
                    />

                </div>
                <div>
                    <span title="visibilité dans l'export">Description : </span>
                    <FontAwesomeIcon
                        icon={faEye}
                        title={currentDisplayDesc ? 'Visible' : 'Invisible'}
                        style={{
                            cursor: isEditing ? 'pointer' : 'not-allowed',
                            opacity: currentDisplayDesc ? 1 : 0.5,
                            color: currentDisplayDesc ? 'green' : 'gray',
                            marginLeft: '5px'
                        }}
                        onClick={() => {
                            if (!isEditing) return;
                            handleFieldChange(
                            label.meta_key,
                            'display_desc',
                            !currentDisplayDesc
                            );
                        }}
                    />
                </div>

                <div>
                {isEditing ? (
                    <>
                        <FontAwesomeIcon
                            icon={faFloppyDisk}
                            title="Sauvegarder"
                            style={{
                                cursor: hasChanges ? 'pointer' : 'not-allowed',
                                color: hasChanges ? 'green' : 'gray',
                                marginLeft: '10px'
                            }}
                            onClick={() => hasChanges && saveField(label.meta_key)}
                        />
                        <FontAwesomeIcon
                            icon={faXmark}
                            title="Annuler"
                            style={{
                                cursor: 'pointer',
                                color: 'red',
                                marginLeft: '15px'
                            }}
                            onClick={() => {
                                setEditIndex(null);
                                // Remove from updatedLabels
                                setUpdatedLabels(prev => {
                                    const copy = { ...prev };
                                    delete copy[label.meta_key];
                                    return copy;
                                });
                                // Reset row and desc to original boolean values from the current label in state
                                setLabels(prev =>
                                    prev.map(l =>
                                        l.meta_key === label.meta_key
                                            ? {
                                                ...l,
                                                display_row: labels.find(lb => lb.meta_key === label.meta_key)?.display_row ?? false,
                                                display_desc: labels.find(lb => lb.meta_key === label.meta_key)?.display_desc ?? false,
                                            }
                                            : l
                                    )
                                );
                            }}
                        />

                    </>
                ) : (
                    <FontAwesomeIcon
                    icon={faPen}
                    title="Modifier"
                    style={{ cursor: 'pointer', color: 'blue', marginLeft: '10px' }}
                    onClick={() => setEditIndex(index)}
                    />
                )}
                </div>
            </div>
        </div>
  );
})}


      <div className="btnrow" style={{ marginTop: '20px' }}>
        <button className="button" onClick={onBack}>Retour</button>
      </div>
    </div>
  );
};

export default AdminRatioLabels;
